import '../styles/home_platform.css'
import '../styles/dashboard.css'
import '../styles/dashboardActions.css'
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import Header2 from '../components/Header2'
import Footer from '../components/Footer'
import SideBar from '../components/SideBar'
import SideResponsiveOpen from '../components/SideResponsiveOpen'
import EditTitleDashboardModal from '../components/EditTitleDashboardModal'
import EditDescriptionDashboardModal from '../components/EditDescriptionDashboardModal'
import EditImageDashboardModal from '../components/EditImageDashboardModal'
import Message from '../components/Message'

import EditWidgetText from '../components/EditWidgetText'
import EditWidgetIndicator from '../components/EditWidgetIndicator'
import EditWidgetSeries from '../components/EditWidgetSeries'

import SeriesWidget from '../components/SeriesWidget'
import TextWidget from '../components/TextWidget'
import IndicatorWidget from '../components/IndicatorWidget'

import DeleteWidgetModal from '../components/DeleteWidgetModal'
import DeleteDashboardModal from '../components/DeleteDashboardModal'

import { WidthProvider, Responsive } from "react-grid-layout";
import {
  getDashboard,
  saveDashboard,
  getWidgets,
  deleteWidget,
  updateViewCount,
  updateDashboardBackgroundColor,
} from '../actions/platformActions'
import {
  acquireDashboardLock,
  heartbeatDashboardLock,
  releaseDashboardLock,
  releaseDashboardLockOnUnload,
} from '../services/dashboardLockApi'
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { OverlayTrigger, Tooltip, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  UPDATE_DASHBOARD_NAME_RESET,
  UPDATE_DASHBOARD_DESCRIPTION_RESET,
  DELETE_WIDGET_RESET,
  DELETE_DASHBOARD_RESET,
  UPDATE_DASHBOARD_IMAGE_RESET,
  UPDATE_DASHBOARD_IMAGE_FAIL,
  UPDATE_DASHBOARD_BACKGROUND_COLOR_RESET,
} from '../constants/platformConstants'
import ViewerModal from '../components/ViewerModal';
import DashboardPresentation from '../components/DashboardPresentation'
import DashboardSharingControls from '../components/DashboardSharingControls'
import ExportDashboardModal from '../components/ExportDashboardModal'
import DashboardVersionHistoryModal from '../components/DashboardVersionHistoryModal'
import { exportDashboard } from '../services/dashboardImportExportApi'
import { exportElementToPdf, slugifyFilename } from '../utils/exportDashboardPdf'

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const DASHBOARD_GRID_PROPS = {
  className: "layout",
  rowHeight: 100,
  cols: { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 },
  breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  containerPadding: [0, 0],
  margin: [20, 20]
};


function DashboardDetailsScreen() {

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { code } = useParams()

  const [editFlag, setEditFlag] = useState(false)
  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin

  // --- Edit lock + version guard ----------------------------------------
  // One session id per mount: it identifies this browser tab specifically,
  // so a second tab opened by the same user is refused the lock rather than
  // silently sharing it and racing against itself.
  const lockSessionIdRef = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now())
  )
  const [lockHeld, setLockHeld] = useState(false)
  const [lockBusy, setLockBusy] = useState(false)
  const [lockHolder, setLockHolder] = useState(null)   // {display_name} when someone else has it
  const lockHeldRef = useRef(false)
  useEffect(() => { lockHeldRef.current = lockHeld }, [lockHeld])

  // base_version for the next save. Seeded from the dashboard detail
  // response and advanced by every successful save; if it ever falls behind
  // the server's current_version, the next save is rejected as stale.
  const [baseVersion, setBaseVersion] = useState(null)
  const [conflict, setConflict] = useState(null)
  // Holds the version we had just before entering edit mode, so the refetch
  // that follows can tell whether someone else saved in the meantime.
  const previousVersionOnEditRef = useRef(null)
  const [refreshedFromOthers, setRefreshedFromOthers] = useState(false)
  const [saveConfirmation, setSaveConfirmation] = useState(false)
  const saveConfirmationTimerRef = useRef(null)

  // --- Version history -----------------------------------------------------
  // The list/preview/restore endpoints are owner-only server-side
  // (docs/dashboard-versioning-design.md section 8). The dashboard detail
  // response now carries the caller's resolved role, so the header entry
  // point is gated on that directly. It used to fire a throwaway request to
  // the owner-only endpoint on every page load purely to see whether it
  // 403'd — an extra round trip per view, and a permission check performed
  // as a side effect.
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)
  const [versionRestoreMessage, setVersionRestoreMessage] = useState(null)
  const versionRestoreMessageTimerRef = useRef(null)

  useEffect(() => () => {
    if (versionRestoreMessageTimerRef.current) clearTimeout(versionRestoreMessageTimerRef.current)
  }, [])

  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef(null);
  const isStickyRef = useRef(false);

  // Anchor for the floating save/edit button — unlike stickyRef (only
  // rendered in edit mode, for the widget palette), this is always present
  // so the floating button can appear on scroll in both Lecture and Edit
  // mode.
  const [pastHeader, setPastHeader] = useState(false);
  const headerAnchorRef = useRef(null);
  const pastHeaderRef = useRef(false);


  useEffect(() => {
    // if (typeof window.trackPageVisit === 'function') {
    //   window.trackPageVisit('Dashboard Details');
    // }

    if (code) {
      dispatch(updateViewCount(code))
      dispatch(getDashboard(code))
      dispatch(getWidgets(code))
    }

    const handleScroll = () => {
      const stickyElement = stickyRef.current;

      if (stickyElement) {
        const stickyTop = stickyElement.getBoundingClientRect().top;
        const nextSticky = stickyTop <= 0;

        if (isStickyRef.current !== nextSticky) {
          isStickyRef.current = nextSticky;
          setIsSticky(nextSticky);
        }
      }

      const headerAnchor = headerAnchorRef.current;
      if (headerAnchor) {
        const nextPastHeader = headerAnchor.getBoundingClientRect().top <= 0;
        if (pastHeaderRef.current !== nextPastHeader) {
          pastHeaderRef.current = nextPastHeader;
          setPastHeader(nextPastHeader);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };

  }, [])

  const dashboardInfo = useSelector(state => state.getDashboard)
  const { loading, error, dashboard } = dashboardInfo

  const dashboardId = dashboard?.id

  // Resolved server-side and returned by the dashboard detail endpoint, so
  // the client never has to guess at authorization. Viewers get no Edit
  // button at all; only the owner sees version history and sharing.
  const dashboardRole = dashboard?.role
  const isOwner = Boolean(dashboard?.is_owner)
  const isCollaborator = Boolean(dashboardRole) && !isOwner
  const canEditDashboard = isOwner || dashboardRole === 'editor'

  const widgetInfo = useSelector(state => state.getWidgets)
  const { loading: loadingWidgets, error: errorWidgets, widgets: widgetsList } = widgetInfo

  const saveInfo = useSelector(state => state.saveDashboard)
  const { loading: loadingSave, error: errorSave, success: successSave, currentVersion: savedVersion, conflict: saveConflict } = saveInfo

  const [initWidgets, setInitWidgets] = useState([])
  const [widgets, setWidgets] = useState([])
  const widgetsRef = useRef([])
  const initWidgetsRef = useRef([])
  const shouldAutoSaveRef = useRef(false)

  // Stable references so the widget list below (React.memo'd) doesn't
  // re-render on every unrelated state change (e.g. the isSticky scroll
  // toggle) — with 30-40 widgets that was the main source of drag/scroll lag.
  const markDashboardDirty = useCallback(() => {
    shouldAutoSaveRef.current = true
  }, [])

  useEffect(() => {
    widgetsRef.current = widgets
  }, [widgets])

  useEffect(() => {
    initWidgetsRef.current = initWidgets
  }, [initWidgets])

  useEffect(() => {
    if (widgetsList && !shouldAutoSaveRef.current) {
      setWidgets(widgetsList.map(item => {
        return {
          id: item.id,
          type: item.widget_type,
          i: item.id,
          x: item.x,
          y: item.y,
          w: item.width,
          h: item.height,
          content: item.content,
        }
      }))
      setInitWidgets(widgetsList.map(item => {
        return {
          id: item.id,
          type: item.widget_type,
          i: item.id,
          x: item.x,
          y: item.y,
          w: item.width,
          h: item.height,
          content: item.content,
        }
      }))

    }
  }, [widgetsList])


  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [dashboardImage, setDashboardImage] = useState('');

  useEffect(() => {
    if (dashboard) {
      setDashboardName(dashboard.dashboard_name)
      setDashboardDescription(dashboard.dashboard_description)
      setDashboardImage(dashboard.image)
      if (dashboard.current_version !== undefined) {
        // Entering edit mode refetches; if the version moved while this page
        // was open, someone else saved and the board the user is about to
        // edit just changed under them. Say so — silently swapping the
        // widgets would be more confusing than the stale copy was.
        if (previousVersionOnEditRef.current !== null
            && previousVersionOnEditRef.current !== undefined
            && dashboard.current_version !== previousVersionOnEditRef.current) {
          setRefreshedFromOthers(true)
        }
        previousVersionOnEditRef.current = null
        setBaseVersion(dashboard.current_version)
      }
    }
  }, [dashboardInfo])

  // Every successful save returns the new current_version. Advancing the
  // local base_version here is what lets a session save more than once —
  // without it the second save would be rejected as stale against a counter
  // the client itself moved.
  useEffect(() => {
    if (savedVersion !== undefined && savedVersion !== null) {
      setBaseVersion(savedVersion)
    }
  }, [savedVersion])

  // A 409 is structural, not a message to show and forget.
  useEffect(() => {
    if (!saveConflict) return
    if (saveConflict.code === 'lock_lost') {
      // Someone took the lock over. Leave edit mode immediately rather than
      // let the user keep making changes that can never be saved.
      setLockHeld(false)
      setEditFlag(false)
      shouldAutoSaveRef.current = false
      setConflict({ kind: 'lock_lost' })
    } else if (saveConflict.code === 'stale_version') {
      setConflict({ kind: 'stale_version', currentVersion: saveConflict.current_version })
    }
  }, [saveConflict])

  const [editTitleModal, setEditTitleModal] = useState(false)
  const [editDescriptionModal, setEditDescriptionModal] = useState(false)

  const [deleteWidgetModal, setDeleteWidgetModal] = useState(false)
  const [deleteDashboardModal, setDeleteDashboardModal] = useState(false)

  const [editImageModal, setEditImageModal] = useState(false)

  const [editTextWidgetModal, setEditTextWidgetModal] = useState(false)
  const [editIndicatorWidgetModal, setEditIndicatorWidgetModal] = useState(false)
  const [editSeriesWidgetModal, setEditSeriesWidgetModal] = useState(false)
  const [selectedWidget, setSelectedWidget] = useState(null)
  const [selectedText, setSelectedText] = useState(null)

  let [key, setKey] = useState('a');

  // const incrementChar = (c) => {
  //     setKey(key + 1);
  // };

  const incrementChar = (c) => {
    setKey(String.fromCharCode(c.charCodeAt(0) + 1))
  }
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (editFlag) {
        event.preventDefault();
        event.returnValue = ''; // Esto muestra la alerta del navegador
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Limpia el evento cuando el componente se desmonta o cuando ya no haya cambios sin guardar
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editFlag]);


  // Lógica de navegación con el sidebar (ejemplo usando navigate() manualmente)
  const handleNavigation = (path) => {
    navigate(path);
  };



  const onLayoutChange = (layout) => {
  };

  const applyLayoutToWidgets = (currentWidgets, nextLayout) => {
    const layoutById = new Map(nextLayout.map(item => [String(item.i), item]))
    return currentWidgets.map(widget => {
      const nextPosition = layoutById.get(String(widget.i))
      if (!nextPosition) return widget

      return {
        ...widget,
        x: nextPosition.x,
        y: nextPosition.y,
        w: nextPosition.w,
        h: nextPosition.h,
      }
    })
  }

  // With compactType={null} + preventCollision={true}, react-grid-layout
  // resolves a collision on the item being dragged/dropped/resized by
  // pushing IT further down by its own height (utils.js: collisionNorth &&
  // compactType == null). That push can (a) cascade far below every other
  // widget, and — confirmed against real production data, where one
  // dashboard ended up with up to 19 widgets saved at the exact same x/y —
  // (b) in some collision sequences leave two widgets at the *exact same*
  // position instead of a clean swap. We don't want to auto-compact the
  // whole board (users leave deliberate gaps between sections), so instead
  // we run a last-resort safety net after every interaction: the widget
  // that was just moved/dropped/resized is nudged down (never touching any
  // other widget's position) until it no longer overlaps anything, capped
  // at a few rows past the current lowest widget so it can't run away.
  const MAX_DROP_GAP_ROWS = 5

  const rectsOverlap = (a, b) => (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  )

  const resolveWidgetPlacement = (widgetsList, targetId) => {
    const target = widgetsList.find(w => String(w.i) === String(targetId))
    if (!target) return widgetsList

    const others = widgetsList.filter(w => String(w.i) !== String(targetId))
    const maxBottom = others.reduce((max, w) => Math.max(max, w.y + w.h), 0)
    const cap = maxBottom + MAX_DROP_GAP_ROWS
    const overlapsOthersAt = (y) => others.some(o => rectsOverlap({ x: target.x, y, w: target.w, h: target.h }, o))

    let y = Math.min(target.y, cap)
    while (y < cap && overlapsOthersAt(y)) {
      y += 1
    }
    // maxBottom is, by definition, at or below every other widget's bottom
    // edge, so landing exactly there can never overlap anything — guaranteed
    // fallback if the board is packed tight enough that no row under the cap
    // was free.
    if (overlapsOthersAt(y)) {
      y = maxBottom
    }

    return y === target.y ? widgetsList : widgetsList.map(widget => (
      String(widget.i) === String(targetId) ? { ...widget, y } : widget
    ))
  }

  // Board-wide overlap guard, run right before every save (manual or auto).
  // resolveWidgetPlacement only protects the ONE widget that was just
  // touched — it can't catch overlaps that arrive some other way (a bulk
  // import, a stale local copy that drifted, etc). This walks the widgets
  // in order and nudges any widget that lands on top of an already-placed
  // one straight down, same rule as resolveWidgetPlacement (and the
  // server-side repair_widget_overlap management command), so a dashboard
  // can never be saved — auto or manual — with two widgets on the same
  // cell.
  const dedupeOverlaps = (widgetsList) => {
    const placed = []
    let changed = false

    const result = widgetsList.map(widget => {
      if (!placed.some(p => rectsOverlap(widget, p))) {
        placed.push(widget)
        return widget
      }

      const maxBottom = placed.reduce((max, p) => Math.max(max, p.y + p.h), 0)
      const cap = maxBottom + MAX_DROP_GAP_ROWS
      const overlapsAt = (y) => placed.some(p => rectsOverlap({ ...widget, y }, p))

      let y = widget.y
      while (y < cap && overlapsAt(y)) {
        y += 1
      }
      if (overlapsAt(y)) {
        y = maxBottom
      }

      changed = changed || y !== widget.y
      const moved = { ...widget, y }
      placed.push(moved)
      return moved
    })

    return changed ? result : widgetsList
  }

  const dropHandler = (layout, layoutItem, event) => {

    let type = event.dataTransfer.getData('Type')
    let content = (type == 'text') ? { body: '', background: false } :
      (type == 'series') ? {} : {}
    let tempItem = {
      id: key,
      i: key,
      type: type,
      x: layoutItem.x,
      y: layoutItem.y,
      // w: layoutItem.w,
      w: 2,
      h: layoutItem.h,
      content: content
    }

    setWidgets(prevState => resolveWidgetPlacement([...applyLayoutToWidgets(prevState, layout), tempItem], tempItem.i))
    markDashboardDirty()
    // console.log(JSON.stringify(layoutItem, ["x", "y", "w", "h"], 2));
    incrementChar(key);
  };

  const closeEditHandler = () => {
    setEditTextWidgetModal(false)
  }

  const closeEditIndicatorHandler = () => {
    setEditIndicatorWidgetModal(false)
  }

  const closeEditSeriesHandler = () => {
    setEditSeriesWidgetModal(false)
  }

  const openEditHandler = useCallback((wid, data) => {
    setSelectedWidget(wid)
    setSelectedText(data)
    setEditTextWidgetModal(true)
  }, [])

  const openEditIndicatorHandler = useCallback((wid) => {
    setSelectedWidget(wid)
    setEditIndicatorWidgetModal(true)
  }, [])

  const openEditSeriesHandler = useCallback((wid) => {
    setSelectedWidget(wid)
    setEditSeriesWidgetModal(true)
  }, [])

  const updateTextHandler = useCallback((wid, text, background, backgroundColor) => {
    setWidgets(prevState => prevState.map(item => (item.i == wid) ? { ...item, content: { body: text, background: background, backgroundColor: backgroundColor } } : item))
    markDashboardDirty()
    setEditTextWidgetModal(false)
  }, [markDashboardDirty])

  const updateSeriesHandler = useCallback((wid, jsonContent) => {
    setWidgets(prevState => prevState.map(item => (item.i == wid) ? { ...item, content: jsonContent } : item))
    markDashboardDirty()
    setEditSeriesWidgetModal(false)
  }, [markDashboardDirty])

  const updateIndicatorHandler = useCallback((wid, content) => {
    setWidgets(prevState => prevState.map(item => (item.i == wid) ? { ...item, content: content } : item))
    markDashboardDirty()
    setEditIndicatorWidgetModal(false)
  }, [markDashboardDirty])

  const openEditTitleHandler = () => {
    dispatch({
      type: UPDATE_DASHBOARD_NAME_RESET
    })
    setEditTitleModal(true)
  }

  const openEditDescriptionHandler = () => {
    dispatch({
      type: UPDATE_DASHBOARD_DESCRIPTION_RESET
    })
    setEditDescriptionModal(true)
  }

  const openEditImageHandler = () => {
    setEditImageModal(true)
  }

  // Standalone color button next to the pencil (no modal): the native color
  // input fires directly on change, and once the reducer reports success we
  // refetch the dashboard so `dashboard.background_color` (and the container
  // styles that depend on it) pick up the new value — same refresh pattern
  // used after the title/description modals close with isEdited=true.
  const dashboardColorInfo = useSelector(state => state.updateDashboardBackgroundColor)
  const { backgroundColor: savedDashboardColor } = dashboardColorInfo

  useEffect(() => {
    if (savedDashboardColor !== undefined) {
      dispatch(getDashboard(code))
      dispatch({ type: UPDATE_DASHBOARD_BACKGROUND_COLOR_RESET })
    }
  }, [savedDashboardColor])

  const changeDashboardColorHandler = (event) => {
    dispatch(updateDashboardBackgroundColor(dashboardId, event.target.value))
  }

  const resetDashboardColorHandler = () => {
    dispatch(updateDashboardBackgroundColor(dashboardId, null))
  }

  const closeEditTitleHandler = (isEdited) => {
    if (isEdited) {
      dispatch(getDashboard(code))
    }
    setEditTitleModal(false)
  }

  const closeEditDescriptionHandler = (isEdited) => {
    if (isEdited) {
      dispatch(getDashboard(code))
    }
    setEditDescriptionModal(false)
  }

  const closeEditImageHandler = (isEdited) => {
    if (isEdited) {
      dispatch({
        type: UPDATE_DASHBOARD_IMAGE_RESET
      })
      dispatch(getDashboard(code))
    }
    setEditImageModal(false)
  }

  const onResizeHandler = (layout, oldItem, newItem) => {
    // console.log('layout: ', layout)
    // console.log('old: ', oldItem)
    // console.log('new: ', newItem)

    setWidgets(prevState => resolveWidgetPlacement(applyLayoutToWidgets(prevState, layout), newItem.i))
    markDashboardDirty()
  }

  const onDragHandler = (layout, oldItem, newItem) => {
    setWidgets(prevState => resolveWidgetPlacement(applyLayoutToWidgets(prevState, layout), newItem.i))
    markDashboardDirty()
  }

  const saveDashboardHandler = async (exitEditMode = true) => {
    const rawWidgets = widgetsRef.current
    const currentWidgets = dedupeOverlaps(rawWidgets)
    if (currentWidgets !== rawWidgets) {
      // Some widget landed on top of another (collision-resolution edge
      // case, a stale drag, etc.) — reflect the nudge in the UI too, not
      // just in what gets sent to the server.
      setWidgets(currentWidgets)
    }
    const savedWidgets = initWidgetsRef.current

    if (JSON.stringify(savedWidgets) != JSON.stringify(currentWidgets)) {

      let newWidgets = currentWidgets.filter(item => isNaN(item.id))
      newWidgets = newWidgets.map(item => {
        return {
          dashboard: dashboardId,
          widget_type: item.type,
          x: item.x,
          y: item.y,
          width: item.w,
          height: item.h,
          content: item.content,
        }
      })

      let updateWidgets = currentWidgets.filter(item => !isNaN(item.id))
      updateWidgets = updateWidgets.map(item => {
        return {
          id: item.id,
          widget_type: item.type,
          x: item.x,
          y: item.y,
          width: item.w,
          height: item.h,
          content: item.content,
        }
      })

      // Awaited on purpose. saveDashboard is an async thunk, so without the
      // await the lock release below would fire while this request is still
      // in flight — and since the release is a tiny DELETE while the save
      // carries the whole widget payload, the release routinely lands first.
      // The server then sees a save from a session that no longer holds the
      // lock and rejects it 409 lock_lost: the edit is silently discarded,
      // no DashboardVersion is written, and current_version never advances,
      // which in turn lets a stale editor's base_version still match and
      // overwrite work that was never actually saved.
      await dispatch(saveDashboard(newWidgets, updateWidgets, {
        dashboardCodeId: code,
        baseVersion,
        lockSessionId: lockSessionIdRef.current,
        // Autosaves may be coalesced into the newest version so a long
        // editing session is one history entry instead of forty; an explicit
        // save always gets its own.
        isAutosave: !exitEditMode,
      }))
    }
    if (exitEditMode) {
      setEditFlag(false)
      await releaseLock()
    }
  }

  useEffect(() => {
    if (successSave) {
      dispatch({
        type: DELETE_WIDGET_RESET,
      })
      // Widgets created in this save only have a real DB id in the server's
      // response — resync from the server so their temporary local id gets
      // replaced. Without this, saving again later would re-submit those
      // same widgets as "new" a second time (they'd still look id-less
      // locally) and silently duplicate them. This used to be gated behind
      // `!editFlag` for the old autosave path, which never got a chance to
      // run since autosave kept editFlag true — that's what caused the
      // duplicate-widget bug on dashboards that stayed open and editing for
      // a while.
      shouldAutoSaveRef.current = false
      dispatch(getWidgets(code))

      // Close the feedback loop: the spinner says a save is happening, this
      // says it landed. Without it a save that succeeds and one that was
      // silently rejected look identical from the user's side — which is
      // exactly how the lock-release race went unnoticed for so long.
      setSaveConfirmation(true)
      if (saveConfirmationTimerRef.current) clearTimeout(saveConfirmationTimerRef.current)
      saveConfirmationTimerRef.current = setTimeout(() => setSaveConfirmation(false), 3000)
    }
  }, [code, dispatch, successSave])

  useEffect(() => () => {
    if (saveConfirmationTimerRef.current) clearTimeout(saveConfirmationTimerRef.current)
  }, [])

  // Autosave: saves in place every 2 minutes while there are unsaved edits,
  // without kicking the user out of edit mode (unlike the manual Save
  // button/floating button, which both exit edit mode on purpose). It's
  // safe to call the exact same saveDashboardHandler used for manual saves
  // — it already runs dedupeOverlaps before building the payload, and the
  // successSave effect above always refetches and clears the ids of any
  // newly-created widgets regardless of editFlag, so a widget saved by
  // autosave can never be resubmitted as "new" on the next cycle.
  const autoSave = async () => {
    // Lock-aware: without the lock the save is guaranteed to 409, so firing
    // it every minute would just spam the endpoint and the user with errors.
    if (shouldAutoSaveRef.current && editFlag && lockHeldRef.current) {
      try {
        saveDashboardHandler(false)
      } catch (error) {
        console.error("Error saving data inside autosave")
      }
    }
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      autoSave()
    }, 60000) // 1 minute

    return () => clearInterval(intervalId)
  }, [editFlag])



  const editDashboardHandler = async () => {
    if (lockBusy) return
    setLockBusy(true)
    setLockHolder(null)
    try {
      await acquireDashboardLock(code, lockSessionIdRef.current, userInfo.access)

      // Always refetch before entering edit mode. This screen loads widgets
      // once on mount, so a page left open while a collaborator edited and
      // saved would start editing from a stale board — and the first save
      // would write those stale widgets back over their work. Refetching
      // here also re-seeds base_version, so the version guard is comparing
      // against what the server actually holds.
      previousVersionOnEditRef.current = baseVersion
      dispatch(getDashboard(code))
      dispatch(getWidgets(code))

      setLockHeld(true)
      setEditFlag(true)
    } catch (error) {
      if (error.response?.status === 409) {
        // Someone else is editing. Surface who, so the user knows whether to
        // wait or go ask them — an anonymous "locked" message is useless.
        setLockHolder(error.response.data?.holder || { display_name: 'Someone' })
      } else {
        setConflict({ kind: 'lock_failed' })
      }
    } finally {
      setLockBusy(false)
    }
  }

  const releaseLock = useCallback(async () => {
    if (!lockHeldRef.current) return
    lockHeldRef.current = false
    setLockHeld(false)
    try {
      await releaseDashboardLock(code, lockSessionIdRef.current, userInfo.access)
    } catch (error) {
      // Best effort: the server's TTL frees it regardless.
    }
  }, [code, userInfo?.access])

  // Restore requires the caller to hold the edit lock (design section 8). If
  // the owner opened version history without already being in edit mode,
  // this acquires it just for the restore call — same lock, same
  // acquireDashboardLock used by editDashboardHandler above, just without
  // flipping the board into edit mode.
  const acquireLockForVersionRestore = useCallback(async () => {
    if (lockHeldRef.current) return { ok: true }
    try {
      await acquireDashboardLock(code, lockSessionIdRef.current, userInfo.access)
      lockHeldRef.current = true
      setLockHeld(true)
      return { ok: true }
    } catch (error) {
      if (error.response?.status === 409) {
        return { ok: false, holder: error.response.data?.holder || { display_name: 'Someone' } }
      }
      return { ok: false }
    }
  }, [code, userInfo?.access])

  const versionHistoryHandler = () => {
    setVersionRestoreMessage(null)
    setVersionHistoryOpen(true)
  }

  const versionRestoredHandler = useCallback((result) => {
    dispatch(getWidgets(code))
    dispatch(getDashboard(code))
    // Every save (a restore included) returns the new current_version — feed
    // it into the same base_version the manual/auto save path advances, or
    // the very next save would be rejected as stale against a counter this
    // session never learned about.
    if (result.current_version !== undefined && result.current_version !== null) {
      setBaseVersion(result.current_version)
    }
    // The lock was only acquired to perform this one restore (see
    // acquireLockForVersionRestore); release it unless the user was already
    // in an editing session before opening version history, in which case
    // it's theirs to keep until they save or leave.
    if (!editFlag) {
      releaseLock()
    }
    if (versionRestoreMessageTimerRef.current) clearTimeout(versionRestoreMessageTimerRef.current)
    setVersionRestoreMessage(
      `Restored to version ${result.targetVersion}: ${result.restored} widget(s) brought back, `
      + `${result.hidden} hidden, ${result.content_reverted} content edit(s) reverted.`
    )
    versionRestoreMessageTimerRef.current = setTimeout(() => setVersionRestoreMessage(null), 8000)
  }, [code, dispatch, editFlag, releaseLock])

  // Heartbeat. A 409 here means the lock was taken over while we held it
  // (a stale-TTL takeover after this tab was suspended, say) — bail out of
  // edit mode at once instead of accumulating unsaveable changes.
  useEffect(() => {
    if (!editFlag || !lockHeld) return undefined

    const intervalId = setInterval(async () => {
      try {
        // Same reason as the pagehide effect below: reading the token from a
        // ref keeps a token refresh from tearing down and restarting this
        // interval, which would silently drop a heartbeat.
        await heartbeatDashboardLock(code, lockSessionIdRef.current, accessTokenRef.current)
      } catch (error) {
        if (error.response?.status === 409) {
          lockHeldRef.current = false
          setLockHeld(false)
          setEditFlag(false)
          shouldAutoSaveRef.current = false
          setConflict({ kind: 'lock_lost' })
        }
      }
    }, 30000) // 30s: three misses before the server's 90s TTL lets anyone else in

    return () => clearInterval(intervalId)
  }, [code, editFlag, lockHeld])

  // The access token is read through a ref, never a dependency. This effect's
  // cleanup releases the lock, and React runs cleanups on every dependency
  // change — not just on unmount. With `userInfo.access` in the dependency
  // list, an ordinary token refresh mid-session (useSessionGate does this
  // automatically as the access token nears expiry) would tear the effect
  // down, release the editor's own lock out from under them, and the next
  // heartbeat would come back 409 "someone else took over" on a dashboard
  // that was never shared with anyone.
  const accessTokenRef = useRef(userInfo?.access)
  useEffect(() => { accessTokenRef.current = userInfo?.access }, [userInfo?.access])

  // Release when the tab goes away. `pagehide` fires in cases `beforeunload`
  // does not (bfcache, mobile task switching), which is why the release is
  // hung off it rather than off the existing unsaved-changes guard.
  useEffect(() => {
    const handlePageHide = () => {
      if (lockHeldRef.current) {
        releaseDashboardLockOnUnload(code, lockSessionIdRef.current, accessTokenRef.current)
      }
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      handlePageHide() // unmounting (navigating away in-app) counts too
    }
  }, [code])

  const deleteWidgetHandler = useCallback((wid) => {
    dispatch({
      type: DELETE_WIDGET_RESET,
    })
    setSelectedWidget(wid)
    setDeleteWidgetModal(true)
  }, [dispatch])

  const closeDeleteWidgetHandler = (isDeleted, wid) => {
    setDeleteWidgetModal(false)
    if (isDeleted) {
      setWidgets(widgets.filter(item => item.id != wid))
      markDashboardDirty()
    }
  }

  const closeDeleteDashboardHandler = (isDeleted, did) => {
    setDeleteDashboardModal(false)
    if (isDeleted) {
      navigate('/dashboard')
    }
  }

  const deleteDashboardHandler = () => {
    dispatch({
      type: DELETE_DASHBOARD_RESET,
    })
    setDeleteDashboardModal(true)
  }

  const [exportError, setExportError] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const presentationRef = useRef(null)

  const exportJsonHandler = async () => {
    setExportError(null)
    setExporting(true)
    try {
      const data = await exportDashboard(code, userInfo.access)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const safeName = slugifyFilename(dashboardName)
      const link = document.createElement('a')
      link.href = url
      link.download = `${safeName}-export.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setExportModalOpen(false)
    } catch (err) {
      setExportError('Unable to export this dashboard. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  // Escape hatch for a lost stale_version race: serialize the LOCAL widget
  // state (the server's copy is someone else's work by now) so the user can
  // reapply it by hand instead of losing the session.
  const downloadLocalCopyHandler = () => {
    const payload = {
      schema_version: 1,
      dashboard_name: dashboardName,
      dashboard_description: dashboardDescription,
      exported_because: 'unsaved-local-copy',
      widgets: widgetsRef.current.map(item => ({
        widget_type: item.type,
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h,
        content: item.content,
      })),
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${slugifyFilename(dashboardName)}-unsaved-copy.json`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const exportPdfHandler = async () => {
    setExportError(null)
    setExporting(true)
    try {
      await exportElementToPdf(presentationRef.current, `${slugifyFilename(dashboardName)}-export.pdf`)
      setExportModalOpen(false)
    } catch (err) {
      setExportError('Unable to export this dashboard as PDF. Please try again.')
    } finally {
      setExporting(false)
    }
  }



  return (
    <div >
      <Header2 />
      <div className='d-flex' style={{ backgroundColor: '#F4F5F7' }}>
        <SideBar onNavigate={handleNavigation} editFlag={editFlag} />
        <div className='dashboard-main-container'>

          <div ref={headerAnchorRef} />

          {/* title */}
          <div className='d-flex align-items-center justify-content-between pb-4'>
            <div className='d-flex align-items-center'>
              {dashboardImage && (
                <img onClick={openEditImageHandler} style={{ cursor: 'pointer', height: '3em', width: 'auto' }} src={`/static/dashboard-icons/${dashboardImage}`} className='pe-4' />
              )}
              <h1 className='p-title me-3'>
                {loading ? (
                  <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
                ) : error ? (
                  <Message variant='danget'>{error}</Message>
                ) : (
                  dashboardName
                )}
              </h1>

              {editFlag && (
                <div className='d-flex align-items-center me-3' style={{ gap: '0.5rem' }}>
                  <OverlayTrigger placement="top" overlay={<Tooltip id="edit-title-tooltip">Edit title</Tooltip>}>
                    <i onClick={openEditTitleHandler} className="fa-solid fa-pen dashboard-action-icon-btn"></i>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip id="edit-color-tooltip">Dashboard background color</Tooltip>}>
                    <label htmlFor="dashboard-color-input" className="fa-solid fa-palette dashboard-action-icon-btn dashboard-action-color"></label>
                  </OverlayTrigger>
                  {dashboard?.background_color && (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="reset-color-tooltip">Reset to default background</Tooltip>}>
                      <i onClick={resetDashboardColorHandler} className="fa-solid fa-arrow-rotate-left dashboard-action-icon-btn"></i>
                    </OverlayTrigger>
                  )}
                  <input
                    id="dashboard-color-input"
                    type="color"
                    value={dashboard?.background_color || '#F4F5F7'}
                    onChange={changeDashboardColorHandler}
                    className='visually-hidden-color-input'
                  />
                </div>
              )}

              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="help-tooltip">
                    Here goes the title.
                  </Tooltip>
                }
              >
                <i className="fa-solid fa-circle-info" style={{ cursor: 'pointer', color: '#A5A5A5', fontSize: '25px' }}></i>
              </OverlayTrigger>

              <OverlayTrigger placement="top" overlay={<Tooltip id="delete-dashboard-tooltip">Delete dashboard</Tooltip>}>
                <i
                  onClick={deleteDashboardHandler}
                  className="fa-solid fa-trash dashboard-action-icon-btn dashboard-action-delete ms-3"
                ></i>
              </OverlayTrigger>

            </div>

            <div className='d-flex align-items-center'>

              {JSON.stringify(widgets) != JSON.stringify(initWidgets) && editFlag && (
                <>
                  <Message variant='danger'>You have unsaved changes</Message>
                  <img src="/static/icons/disk.svg" className='ps-3' style={{ height: '25px', width: 'auto' }} alt="" />
                </>
              )}
            </div>
          </div>

          {/* Descripcion */}
          <div className='d-flex align-items-center'>
            <p className='p-paragraph mb-0 pe-4' style={{ color: '#A5A5A5' }}>
              {loading ? (
                <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
              ) : error ? (
                <Message variant='danget'>{error}</Message>
              ) : (
                dashboardDescription
              )}
            </p>
            {editFlag && (
              <OverlayTrigger placement="top" overlay={<Tooltip id="edit-description-tooltip">Edit description</Tooltip>}>
                <i onClick={openEditDescriptionHandler} className="fa-solid fa-pen dashboard-action-icon-btn me-3"></i>
              </OverlayTrigger>
            )}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="help-tooltip">
                  Here goes the description.
                </Tooltip>
              }
            >
              <i className="fa-solid fa-circle-info me-2" style={{ cursor: 'pointer', color: '#A5A5A5', fontSize: '25px' }}></i>
            </OverlayTrigger>
          </div>

          {/* !!!!!!!!!!!!!!!!! WIDGETS */}

          {errorSave && (
            // saveDashboard's error was captured in redux (`errorSave`) but
            // never rendered anywhere — a rejected save (e.g. the backend
            // returning a 400 on one widget's content) used to fail
            // completely silently. Surfacing it here so a failed save is
            // visible instead of looking like "I edited something and it
            // just didn't stick".
            <Message variant='danger'>Dashboard could not be saved: {errorSave}</Message>
          )}

          {versionRestoreMessage && (
            <Message variant='success'>{versionRestoreMessage}</Message>
          )}

          <div className='d-flex justify-content-between align-items-center pt-3 pb-3'>
            <div className='d-flex align-items-center'>
              {editFlag && (
                <>

                  <h1 className='p-subtitle'>Widgets</h1>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="help-tooltip">
                        Drag and drop one of the following widgets into the dashboard area.
                      </Tooltip>
                    }
                  >
                    <i className="fa-solid fa-circle-info ps-3" style={{ cursor: 'pointer', color: '#A5A5A5', fontSize: '25px' }}></i>
                  </OverlayTrigger>
                </>
              )}
            </div>
            <div className='d-flex align-items-center'>
              {!editFlag && (
                <>
                  <i className="fa-solid fa-eye pe-2"></i>
                  <p style={{ fontSize: '16px' }} className='m-0'>Lecture Mode</p>
                </>
              )}
              {!editFlag && <DashboardSharingControls code={code} access={userInfo.access} />}
              {editFlag && (
                <>
                  <i className="fa-solid fa-pen-to-square pe-2"></i>
                  <p style={{ fontSize: '16px' }} className='m-0'>Edit Mode</p>
                </>
              )}
            </div>
            <div className='me-3 d-flex align-items-center'>
              {isOwner && (
                <div
                  onClick={versionHistoryHandler}
                  className='dashboard-export-text-btn me-2'
                >
                  <i className="fa-solid fa-clock-rotate-left"></i>
                  Version History
                </div>
              )}
              <div
                onClick={() => { setExportError(null); setExportModalOpen(true) }}
                className='dashboard-export-text-btn me-2'
              >
                <i className="fa-solid fa-file-export"></i>
                Export Dashboard
              </div>
              {editFlag ? (
                <div
                  style={{ width: '10em', pointerEvents: loadingSave ? 'none' : 'auto', opacity: loadingSave ? 0.75 : 1 }}
                  onClick={loadingSave ? undefined : () => saveDashboardHandler(true)}
                  className='btn btn-dark text-white text-nowrap'
                  aria-busy={loadingSave ? 'true' : 'false'}
                >
                  {loadingSave ? (
                    <>
                      <Spinner animation='border' as={'span'} style={{ width: '16px', height: '16px' }} className='me-2' />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-floppy-disk pe-2 text-white"></i>
                      Save Dashboard
                    </>
                  )}
                </div>
              ) : !canEditDashboard ? null : lockHolder ? (
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip id='dashboard-lock-tooltip'>Only one person can edit a dashboard at a time.</Tooltip>}
                >
                  <div onClick={editDashboardHandler} className='btn btn-outline-secondary text-nowrap'>
                    <i className="fa-solid fa-lock pe-2"></i>
                    {lockHolder.display_name} is editing — Retry
                  </div>
                </OverlayTrigger>
              ) : (
                <div onClick={editDashboardHandler} className='btn btn-dark text-white'>
                  {lockBusy ? (
                    <Spinner animation='border' as={'span'} style={{ width: '16px', height: '16px' }} />
                  ) : (
                    <><i className="fa-solid fa-pen-to-square text-white pe-2"></i>Edit Dashboard</>
                  )}
                </div>
              )}
            </div>
          </div>
          {isCollaborator && dashboard?.owner_display_name && (
            <div className='dashboard-shared-chip' role='note'>
              <i className="fa-solid fa-user-group pe-2" aria-hidden='true'></i>
              Shared by {dashboard.owner_display_name}
              {dashboardRole === 'viewer' && <span className='ps-1'>· view only</span>}
            </div>
          )}
          {saveConfirmation && (
            <Message variant='success'>
              <i className="fa-solid fa-circle-check pe-2" aria-hidden='true'></i>
              Dashboard saved.
            </Message>
          )}
          {refreshedFromOthers && (
            <Message variant='info'>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <span>This dashboard was updated by someone else. It has been refreshed, so you're editing the latest version.</span>
                <button type='button' className='btn btn-sm btn-outline-dark' onClick={() => setRefreshedFromOthers(false)}>
                  Got it
                </button>
              </div>
            </Message>
          )}
          {editFlag && lockHeld && (
            <div className='dashboard-lock-banner' role='status'>
              <i className="fa-solid fa-pen-nib pe-2" aria-hidden='true'></i>
              You're editing. Others can't edit until you save or leave.
            </div>
          )}
          {conflict && (
            <Message variant={conflict.kind === 'stale_version' ? 'warning' : 'danger'}>
              {conflict.kind === 'lock_lost' && (
                <>Someone else took over editing this dashboard, so editing was stopped here. Reload to see their changes.</>
              )}
              {conflict.kind === 'lock_failed' && (
                <>Editing couldn't be started right now. Please try again.</>
              )}
              {conflict.kind === 'stale_version' && (
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <span>
                    This dashboard was changed by someone else, so your unsaved changes can't be applied.
                  </span>
                  <button type='button' className='btn btn-sm btn-dark' onClick={() => window.location.reload()}>
                    Reload
                  </button>
                  {/* The local widget state is the only copy of the user's
                      work at this point, so offer it as a file before the
                      reload throws it away. */}
                  <button type='button' className='btn btn-sm btn-outline-dark' onClick={downloadLocalCopyHandler}>
                    Download my copy
                  </button>
                </div>
              )}
            </Message>
          )}
          {editFlag && (
            <div ref={stickyRef} className={`default-widgets-container ${isSticky ? "sticky-active" : ""}`}>


              <div
                className="default-widget"
                draggable={true}
                unselectable="on"
                // this is a hack for firefox
                // Firefox requires some kind of initialization
                // which we can do by adding this attribute
                // @see https://bugzilla.mozilla.org/show_bug.cgi?id=568313
                onDragStart={e => e.dataTransfer.setData("Type", "text")}
              >
                <Row className='w-100 h-100'>
                  <Col className='default-widget-left'>
                    <p className='default-widget-title'>Text</p>
                    <p className='default-widget-description'>Card with text for descriptions or annotations</p>
                  </Col>
                  <Col className='d-flex align-items-center justify-content-center'>
                    <p className='default-widget-content m-0'>My Text</p>
                  </Col>
                </Row>
              </div>


              <div
                className="default-widget"
                draggable={true}
                unselectable="on"
                // this is a hack for firefox
                // Firefox requires some kind of initialization
                // which we can do by adding this attribute
                // @see https://bugzilla.mozilla.org/show_bug.cgi?id=568313
                onDragStart={e => e.dataTransfer.setData("Type", "indicator")}
              >
                <Row className='w-100 h-100'>
                  <Col className='default-widget-left'>
                    <p className='default-widget-title'>Indicator</p>
                    <p className='default-widget-description'>Card with main figure of one indicator</p>
                  </Col>
                  <Col className='d-flex align-items-center justify-content-center flex-column'>
                    <p className='dwidget-indicator-title'>Indicator</p>
                    {/* <p className='dwidget-indicator-country'>Argentina</p> */}
                    <p className='dwidget-indicator-value'>54</p>
                    {/* <p className='dwidget-indicator-unit'>unit - (country)</p> */}

                  </Col>
                </Row>
              </div>


              <div
                className="default-widget"
                draggable={true}
                unselectable="on"
                // this is a hack for firefox
                // Firefox requires some kind of initialization
                // which we can do by adding this attribute
                // @see https://bugzilla.mozilla.org/show_bug.cgi?id=568313
                onDragStart={e => e.dataTransfer.setData("Type", "series")}
              >
                <Row className='w-100 h-100'>
                  <Col className='default-widget-left'>
                    <p className='default-widget-title'>Series</p>
                    <p className='default-widget-description'>Card with one specific indicator chart </p>
                  </Col>
                  <Col className='d-flex align-items-center justify-content-center'>
                    {/* <p className='default-widget-content'>My Text</p> */}
                    <img src="/static/dashboard-widget-series.svg" />
                  </Col>
                </Row>
              </div>



            </div>
          )}
          {/* Scoped to just the widgets area (not the sidebar, header, or
              title/description above) — coloring the whole page looked
              wrong, this is what the color picker is actually meant to
              theme: the series/indicator/text cards themselves. */}
          <div
            className="border-top my-3 pt-4 px-3 pb-3"
            style={dashboard?.background_color ? { backgroundColor: dashboard.background_color, borderRadius: '8px' } : undefined}
          >
            {loadingWidgets && widgets.length === 0 ? (
            <div className='w-100 d-flex justify-content-center'>
                <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
              </div>
            ) : (
            editFlag ? <ResponsiveReactGridLayout
                id="react-grid-layout"
                {...DASHBOARD_GRID_PROPS}
                className={`${editFlag && 'dashboard-edit-container'} hhuu`}
                compactType={null}
                // cols={{lg: 2}}
                // breakpoints={{lg: 1200}}
                // style={{ background: "red" }}
                // layouts={layouts}
                // onDragStart={(layout, oldItem, newItem, placeholder) => {
                //   placeholder.style.backgroundColor = 'green'
                // }}
                measureBeforeMount={false}
                isResizable={editFlag}
                isDraggable={editFlag}
                draggableCancel=".dwidget-icon-action, .viewer-container, .modal-container, input, textarea, select, button, .custom-select-container"
                isEdited={editFlag}
                isDroppable={editFlag}
                // isBounded={editFlag}
                // useCSSTransforms={mounted}
                // compactType={compactType}
                preventCollision={true}
                onLayoutChange={onLayoutChange}
                onDragStop={onDragHandler}
                onResizeStop={onResizeHandler}
                // onBreakpointChange={onBreakpointChange}
                onDrop={dropHandler}
              // isDroppable
              >
                {widgets.length == 0 && (
                  <div className='d-none' key={'default'} data-grid={{ x: 6, y: 1, w: 1, h: 1 }}>
                  </div>
                )}
                {widgets.length > 0 && widgets.map(item => (
                  <div key={item.i} data-grid={{ x: item.x, y: item.y, w: item.w, h: item.h, resizeHandles: ['sw', 'nw', 'se', 'ne'] }}>
                    {item.type == 'series' ? (
                      <SeriesWidget id='react-grid-item' editFlag={editFlag} wid={item.i} data={item.content} gridHeight={item.h} openCallback={openEditSeriesHandler} deleteCallback={deleteWidgetHandler} updateCallback={updateSeriesHandler} />
                    ) : item.type == 'text' ? (
                      <TextWidget id='react-grid-item' editFlag={editFlag} wid={item.i} data={item.content} openCallback={openEditHandler} deleteCallback={deleteWidgetHandler} />
                    ) : (
                      <IndicatorWidget id='react-grid-item' editFlag={editFlag} wid={item.id} data={item.content} openCallback={openEditIndicatorHandler} deleteCallback={deleteWidgetHandler} />
                    )}
                  </div>
                ))}

            </ResponsiveReactGridLayout> : (
              // Design decision: the PDF export mirrors the dashboard's chosen
              // background instead of staying hardcoded white, so the export
              // matches what the owner configured and sees on screen.
              <div ref={presentationRef} style={{ backgroundColor: dashboard?.background_color || '#ffffff' }}>
                <DashboardPresentation widgets={widgets} backgroundColor={dashboard?.background_color} />
              </div>
            )
            )}
          </div>

        </div>
      </div>

      <SideResponsiveOpen />
      <Footer />

      {pastHeader && (
        <OverlayTrigger placement="left" overlay={<Tooltip id="floating-save-tooltip">{loadingSave ? 'Saving…' : editFlag ? 'Save Dashboard' : 'Edit Dashboard'}</Tooltip>}>
          <button
            type="button"
            disabled={loadingSave || lockBusy}
            aria-busy={loadingSave ? 'true' : 'false'}
            onClick={editFlag ? () => saveDashboardHandler(true) : editDashboardHandler}
            className={`dashboard-floating-save-btn ${editFlag && JSON.stringify(widgets) != JSON.stringify(initWidgets) ? 'dashboard-floating-save-btn-dirty' : ''}`}
          >
            {(loadingSave || lockBusy) ? (
              <Spinner animation='border' as={'span'} style={{ width: '16px', height: '16px' }} />
            ) : (
              <i className={`fa-solid ${editFlag ? 'fa-floppy-disk' : 'fa-pen-to-square'}`}></i>
            )}
          </button>
        </OverlayTrigger>
      )}

      {editTextWidgetModal && (
        <EditWidgetText closeCallback={closeEditHandler} updateCallback={updateTextHandler} data={selectedText} wid={selectedWidget} />
      )}
      {editIndicatorWidgetModal && (
        <EditWidgetIndicator closeCallback={closeEditIndicatorHandler} updateCallback={updateIndicatorHandler} data={widgets?.find(item => item.id == selectedWidget || item.i == selectedWidget)?.content || {}} wid={selectedWidget} />
      )}
      {editSeriesWidgetModal && (
        <EditWidgetSeries closeCallback={closeEditSeriesHandler} updateCallback={updateSeriesHandler} data={widgets?.find(item => item.id == selectedWidget || item.i == selectedWidget)?.content || {}} wid={selectedWidget} />
      )}



      {editTitleModal && (
        <EditTitleDashboardModal closeCallback={closeEditTitleHandler} title={dashboard?.dashboard_name} id={dashboardId} />
      )}

      {editDescriptionModal && (
        <EditDescriptionDashboardModal closeCallback={closeEditDescriptionHandler} description={dashboard?.dashboard_description} id={dashboardId} />
      )}

      {editImageModal && (
        <EditImageDashboardModal closeCallback={closeEditImageHandler} image={dashboard?.image} id={dashboardId} />
      )}

      {deleteWidgetModal && (
        <DeleteWidgetModal closeCallback={closeDeleteWidgetHandler} wid={selectedWidget} did={dashboardId} />
      )}
      {deleteDashboardModal && (
        <DeleteDashboardModal closeCallback={closeDeleteDashboardHandler} did={dashboardId} />
      )}
      {exportModalOpen && (
        <ExportDashboardModal
          closeCallback={() => setExportModalOpen(false)}
          onExportJson={exportJsonHandler}
          onExportPdf={exportPdfHandler}
          pdfDisabled={editFlag}
          busy={exporting}
          error={exportError}
        />
      )}
      {versionHistoryOpen && (
        <DashboardVersionHistoryModal
          code={code}
          access={userInfo.access}
          lockSessionId={lockSessionIdRef.current}
          lockHeld={lockHeld}
          acquireLock={acquireLockForVersionRestore}
          onRestored={versionRestoredHandler}
          closeCallback={() => setVersionHistoryOpen(false)}
        />
      )}
    </div>
  )
}

export default DashboardDetailsScreen
